import ChevronLeft from '@mdi/svg/svg/chevron-left.svg?react';
import ChevronRight from '@mdi/svg/svg/chevron-right.svg?react';
import { type SiteId } from '@solinkcorp/api-types';
import { DateTimePickerModal, MDIIcon, Popover } from '@solinkcorp/solink-components';
import { isToday as checkIsToday, getLocalTimeZone } from '@solinkcorp/utils';
import { ALL_LOCATIONS, HISTOGRAM_RANGE } from '@solinkcorp/vd-common';
import { decrementDate, incrementDate, setFilterDate } from 'actions/filterActions';
import classNames from 'classnames';
import { CommonContext } from 'components/AuthenticatedApp/AuthenticatedApp';
import { areCapsulesFilteredByDateTimeField } from 'components/EventFilter/utils';
import CustomDateTimeInput from 'components/ModalDateTimePicker/CustomDateTimeInput';
import { useModalDateTimePicker } from 'components/ModalDateTimePicker/useModalDateTimePicker';
import { usePlayingCamerasContext } from 'components/PlayingCamerasContext';
import AnalyticsLogger from 'models/AnalyticsLogger';
import moment from 'moment';
import React, { useContext, useRef, useState } from 'react';
import { useControllerState } from 'solox';
import useFluxStore from 'src/hooks/useFluxStore';
import useLocale from 'src/hooks/useLocale';
import CameraStore from 'stores/CameraStore';
import EventFilterStore from 'stores/EventFilterStore';
import { LocationStore } from 'stores/LocationStore';
import { useDispatch, useSelector } from 'stores/store';
import { useEventStandard } from 'utils/EventStandardCache/eventStandardCache';
import FeaturesUtil from 'utils/FeaturesUtil';
import { type CameraGroupWithCameras } from 'utils/types';
import './GlobalDatePicker.sass';

interface GlobalDatePickerProps {
  className?: string;
  colorMode?: 'light' | 'dark';
  /** Called to toggle off-live. */
  handleToggleClick?: () => void;
  /** False if the live toggle is currently on. */
  isOnInToggle?: boolean;
  minDate: Date | null | undefined;
  maxDate?: Date | null | undefined;
  /** Called when the selected day changes. */
  onChangeRangeDate?: () => void;
  /**
   * Called whenever the user commits a date through this picker - the modal or
   * the chevrons - even if it lands on the day that was already selected.
   * Unlike `onChangeRangeDate` this reports the interaction, not the change.
   */
  onDatePicked?: () => void;
  selectedGroup?: CameraGroupWithCameras;
  selectedLocationIds: readonly SiteId[];
  showLiveToggle?: boolean;
  showTimeInput?: boolean;
  disabled?: boolean;
  /** True when the prev/next chevrons step by the selected range instead of by day. */
  navigateByRange: boolean;
}

const GlobalDatePicker: React.FC<GlobalDatePickerProps> = (props) => {
  const {
    className,
    colorMode = 'dark',
    handleToggleClick,
    isOnInToggle,
    selectedLocationIds,
    showLiveToggle,
    showTimeInput,
    navigateByRange,
  } = props;

  const dispatch = useDispatch();
  const locale = useLocale();

  const {
    date,
    range,
    cameraGroupId,
    timezoneId: timezoneIdOverride,
  } = useSelector((state) => state.filter);

  const { modalState, open, close } = useModalDateTimePicker(date);

  const playingCamerasController = usePlayingCamerasContext();
  const playingCameraIds = useControllerState(
    playingCamerasController.state,
    (state) => state.playingCameraIds,
  );

  const eventStandard = useEventStandard();
  const fieldStats = useSelector((state) => state.fieldStats.fieldStats);
  const capsules = useFluxStore(EventFilterStore, (store) => store.getAppState().capsules);

  const cameraGroups: CameraGroupWithCameras[] = useFluxStore(CameraStore, (store) =>
    store.getCameraGroups(),
  );

  const { firstDayOfWeek } = useContext(CommonContext).authData;
  // If we don't have the event standard yet, disable the global date picker if there
  // are any capsules at all.  It will get sorted out when the event standard loads.
  const disabledByFilters =
    eventStandard && fieldStats
      ? areCapsulesFilteredByDateTimeField(eventStandard, fieldStats, capsules)
      : capsules.length;

  const disabled = props.disabled || disabledByFilters;

  const popoverTimeout = useRef<number | undefined>();
  const [showPopover, setShowPopver] = useState(false);

  const selectedGroup = cameraGroups?.find(
    (group: CameraGroupWithCameras) => group.id === cameraGroupId,
  );

  let selectedLocationId: SiteId = ALL_LOCATIONS as SiteId;

  /*
    Choose the location, so we can display time in the desired timezone
    Scenario 1: Playing video, use the timezone of the first playing camera's location
    Scenario 2: A camera group is selected, use the timezone of the first camera's location in the list
    Scenario 3: One location is selected, use it's timezone
    Note: If there is more than one location LocationStore.getLocation() returns undefined
  */
  if (playingCameraIds?.[0]?.locationId) {
    selectedLocationId = playingCameraIds[0].locationId;
  } else if (selectedGroup?.cameras[0]?.siteId) {
    selectedLocationId = selectedGroup.cameras[0].siteId;
  } else if (selectedLocationIds?.length === 1) {
    selectedLocationId = selectedLocationIds[0] as SiteId;
  }

  const location = LocationStore.getLocation(selectedLocationId);
  let timezone = location?.timezone ?? getLocalTimeZone();

  // More than one location is select via LocationSelector.
  if (selectedLocationIds?.length > 1) {
    timezone = timezoneIdOverride;
  }

  // Convert a null to undefined.
  const minDate = props.minDate ?? undefined;
  const maxDate = props.maxDate ?? undefined;

  const isToday = checkIsToday(date, timezone);
  const isOffInToggle = showLiveToggle && isOnInToggle === false;
  const isPrevDisabled = minDate && moment(date).isSameOrBefore(minDate, 'day');

  const datePickerContainerClass = classNames(
    className,
    'date-picker-container',
    `mode-${colorMode}`,
    {
      filtered: !isToday,
      'in-toggle': showLiveToggle,
      'on-in-toggle': isOnInToggle,
      transparent: showLiveToggle,
      disabled,
    },
  );
  const chevronLeftIconClass = classNames('chevron-left', {
    disabled: isPrevDisabled || disabled,
  });
  const chevronRightIconClass = classNames('chevron-right', {
    disabled: isToday || disabled,
  });

  const onMouseOver = () => {
    const { disabled, isOnInToggle } = props;

    if (!isOnInToggle && disabled) {
      if (popoverTimeout.current !== undefined) {
        clearTimeout(popoverTimeout.current);
        popoverTimeout.current = undefined;
      }

      setShowPopver(true);
    }
  };

  const onMouseOut = () => {
    if (!showPopover) {
      return;
    }
    popoverTimeout.current = window.setTimeout(() => setShowPopver(false));
  };

  const handleDatePickerClickShow = () => {
    open();
    AnalyticsLogger.openDatePicker({ isOpen: true });
  };

  const handleDatePickerChange = (value: Date | undefined) => {
    close();

    let dateToChange: Date | undefined = value;
    if (minDate && value && value < minDate) {
      dateToChange = minDate;
    }

    if (!moment.tz(dateToChange, timezone).isSame(date, 'day')) {
      props.onChangeRangeDate?.();
    }

    if (dateToChange !== undefined) {
      props.onDatePicked?.();
      dispatch(setFilterDate(dateToChange.valueOf()));
      AnalyticsLogger.selectDateViaDatePicker({ date: dateToChange });
    }
  };

  const _navPrevRange = () => {
    const { minDate } = props;
    props.onDatePicked?.();
    props.onChangeRangeDate?.();
    dispatch(decrementDate({ unit: navigateByRange ? range : HISTOGRAM_RANGE.DAY, minDate }));
  };

  const _navNextRange = () => {
    props.onDatePicked?.();
    props.onChangeRangeDate?.();
    dispatch(incrementDate({ unit: navigateByRange ? range : HISTOGRAM_RANGE.DAY }));
  };

  return (
    <div
      id="global-date-picker"
      data-intercom-target="global-date-picker"
      className={datePickerContainerClass}
      onClick={isOffInToggle && !disabled ? handleToggleClick : undefined}
      onMouseEnter={onMouseOver}
      onMouseOut={onMouseOut}
      data-testid="date-picker-container"
    >
      <span
        className={chevronLeftIconClass}
        data-testid="date-picker-prev-day"
        onClick={_navPrevRange}
      >
        <MDIIcon icon={ChevronLeft} />
      </span>
      <div className="date-picker">
        <CustomDateTimeInput
          locale={locale}
          timezoneId={timezone}
          showTime={!!showTimeInput}
          value={modalState.editingDate ?? date}
          onClick={handleDatePickerClickShow}
        />
        {modalState.isOpen && (
          <DateTimePickerModal
            calendarStartDay={firstDayOfWeek}
            locale={locale}
            disallowFuture
            minTime={minDate}
            maxTime={maxDate}
            name="date"
            onChange={handleDatePickerChange}
            onCancel={close}
            showTimeInput={showTimeInput}
            timeZoneId={timezone}
            value={modalState.editingDate}
            shouldCloseOnSelect={false}
          />
        )}
        <Popover
          className="global-date-filter-popover"
          horizontalOffset={-140}
          horizontalOffsetToMiddle={true}
          horizontalPosition="right"
          isOverlayVisible={false}
          isOpen={showPopover}
          parent="#global-date-picker"
          verticalPosition="bottom"
        >
          <div className="message">
            {FeaturesUtil.isLiveViewOnly()
              ? locale.playerMessage.liveViewOnlyRestricted
              : locale.playerMessage.controlRestricted}
          </div>
        </Popover>
      </div>
      <span
        className={chevronRightIconClass}
        data-testid="date-picker-prev-day"
        onClick={_navNextRange}
      >
        <MDIIcon icon={ChevronRight} />
      </span>
    </div>
  );
};

export default GlobalDatePicker;
